import { gql } from '@apollo/client';

import {
  KOMMO_LEAD_BOARD_FIELDS,
  KOMMO_LEAD_CARD_FIELDS,
  KOMMO_LEAD_ROW_FIELDS,
  KOMMO_NOTE_FIELDS,
  KOMMO_STAGE_FIELDS,
  KOMMO_SYNC_STATUS_FIELDS,
  KOMMO_USER_FIELDS,
  LEAD_CONVERSATION_FIELDS,
  LEAD_LADDER_RUN_FIELDS,
  LEAD_SMS_STATE_FIELDS,
} from '../fragments/crm';
import { SMS_PREVIEW_FIELDS } from '../fragments/sms';

/**
 * ТЗ 0084 — лиды Kommo в ERP. Имена операций намеренно отличаются от полей
 * бэкенда (`kommoLeads` → `KommoLeadList` и т. д.): иначе codegen склеил бы
 * тип результата с типом поля.
 */

/** Список лидов по фильтрам: страница, всего, источник, последняя синхронизация. */
export const KommoLeadListQuery = gql`
  ${KOMMO_LEAD_ROW_FIELDS}
  query KommoLeadList(
    $statusIds: [ID!]
    $responsible: String
    $search: String
    $includeClosed: Boolean
    $ladder: String
    $utmSource: [String!]
    $utmMedium: [String!]
    $utmCampaign: [String!]
    $fundingStatus: [String!]
    $hasFunding: Boolean
    $hasStudent: Boolean
    $createdFrom: Date
    $sort: String
    $offset: Int
    $limit: Int
  ) {
    kommoLeads(
      statusIds: $statusIds
      responsible: $responsible
      search: $search
      includeClosed: $includeClosed
      ladder: $ladder
      utmSource: $utmSource
      utmMedium: $utmMedium
      utmCampaign: $utmCampaign
      fundingStatus: $fundingStatus
      hasFunding: $hasFunding
      hasStudent: $hasStudent
      createdFrom: $createdFrom
      sort: $sort
      offset: $offset
      limit: $limit
    ) {
      items {
        ...KommoLeadRowFields
      }
      total
      source
      syncedAt
    }
  }
`;

/**
 * ТЗ 0086 — значения меток и этапов дофинансирования со счётчиками по ВСЕМУ
 * зеркалу (а не по отфильтрованной ленте): после выбора значения варианты
 * фильтра не схлопываются до него одного. `value: ''` — «без метки».
 */
export const KommoLeadFacetsQuery = gql`
  query KommoLeadFacets($includeClosed: Boolean) {
    kommoLeadFacets(includeClosed: $includeClosed) {
      utmSource {
        value
        count
      }
      utmMedium {
        value
        count
      }
      utmCampaign {
        value
        count
      }
      fundingStatus {
        value
        count
      }
      withFunding
      withStudent
    }
  }
`;

/** Этапы воронки «New leads» и число открытых лидов на каждом — чипы фильтра. */
export const KommoLeadStageListQuery = gql`
  ${KOMMO_STAGE_FIELDS}
  query KommoLeadStageList {
    kommoLeadStages {
      ...KommoStageFields
      leadsCount
    }
  }
`;

/** Карточка лида. */
export const KommoLeadCardQuery = gql`
  ${KOMMO_LEAD_CARD_FIELDS}
  query KommoLeadCard($id: ID!) {
    kommoLead(id: $id) {
      ...KommoLeadCardFields
    }
  }
`;

/**
 * ТЗ 0090 — лид договора в карточке договора: только то, что нужно блоку
 * лесенок (можно ли запускать и почему лесенка не пошлёт). Та же запись кэша
 * `KommoLeadType:<id>`, что у карточки лида: действия с лесенками
 * перечитывают карточку лида, и блок обновляется сам.
 */
export const ContractLeadLadderHeadQuery = gql`
  query ContractLeadLadderHead($id: ID!) {
    kommoLead(id: $id) {
      id
      personName
      isClosed
      inPipeline
      isDeleted
      ladderReadiness {
        kind
        warning
        firstSms
        firstSmsSegments
      }
    }
  }
`;

/**
 * Примечания Kommo (вживую, кэш сервера 60 с) и наши, ещё не записанные.
 * Kommo недоступен — `error`, а наши примечания приходят всё равно.
 */
export const KommoLeadNoteListQuery = gql`
  ${KOMMO_NOTE_FIELDS}
  query KommoLeadNoteList($leadId: ID!) {
    kommoLeadNotes(leadId: $leadId) {
      items {
        ...KommoNoteFields
      }
      error {
        code
        message
      }
    }
  }
`;

/** Журнал карточки лида: SMS ушла / не ушла, примечание не записалось. */
export const KommoLeadEventListQuery = gql`
  query KommoLeadEventList($leadId: ID!) {
    kommoLeadEvents(leadId: $leadId) {
      id
      kind
      actorName
      createdAt
      messageId
      code
    }
  }
`;

/** SMS в карточке лида: можно ли, куда уйдёт, история. */
export const LeadSmsStateQuery = gql`
  ${LEAD_SMS_STATE_FIELDS}
  query LeadSmsState($leadId: ID!) {
    kommoLeadSms(leadId: $leadId) {
      ...LeadSmsStateFields
    }
  }
`;

/** Живой предпросмотр SMS лиду. */
export const LeadSmsPreviewQuery = gql`
  ${SMS_PREVIEW_FIELDS}
  query LeadSmsPreview($leadId: ID!, $text: String!, $link: LeadSmsLinkInput) {
    previewLeadSms(leadId: $leadId, text: $text, link: $link) {
      ...SmsPreviewFields
    }
  }
`;

/** Этап 2: запуски SMS-лесенок лида (последние 10), новые сверху. */
export const KommoLeadLadderListQuery = gql`
  ${LEAD_LADDER_RUN_FIELDS}
  query KommoLeadLadderList($leadId: ID!) {
    kommoLeadLadders(leadId: $leadId) {
      ...LeadLadderRunFields
    }
  }
`;

/** Этап 3: треды Gmail с лидом — лента «Переписки» карточки. */
export const KommoLeadThreadListQuery = gql`
  ${LEAD_CONVERSATION_FIELDS}
  query KommoLeadThreadList($leadId: ID!) {
    kommoLeadConversations(leadId: $leadId) {
      ...LeadConversationFields
    }
  }
`;

/** Admin: пользователи Kommo и сотрудники ERP за ними. */
export const KommoUserListQuery = gql`
  ${KOMMO_USER_FIELDS}
  query KommoUserList {
    kommoUsers {
      ...KommoUserFields
    }
  }
`;

/** Состояние синхронизации (прогоны и ошибка — только Admin). */
export const KommoSyncStateQuery = gql`
  ${KOMMO_SYNC_STATUS_FIELDS}
  query KommoSyncState {
    kommoSyncStatus {
      ...KommoSyncStatusFields
    }
  }
`;

/** SMS в карточке кандидата дофинансирования. */
export const CandidateSmsStateQuery = gql`
  ${LEAD_SMS_STATE_FIELDS}
  query CandidateSmsState($candidateId: ID!) {
    fundingCandidateSms(candidateId: $candidateId) {
      ...LeadSmsStateFields
    }
  }
`;

/** Живой предпросмотр SMS кандидату дофинансирования. */
export const CandidateSmsPreviewQuery = gql`
  ${SMS_PREVIEW_FIELDS}
  query CandidateSmsPreview(
    $candidateId: ID!
    $text: String!
    $link: LeadSmsLinkInput
  ) {
    previewFundingCandidateSms(
      candidateId: $candidateId
      text: $text
      link: $link
    ) {
      ...SmsPreviewFields
    }
  }
`;

/**
 * Задача #116: лиды в палитре команд ⌘K. Переменная называется `$query` —
 * как у остальных пяти поисков палитры: по `variables.query` палитра
 * отбрасывает ответы на прошлое нажатие клавиши. Закрытые лиды тоже ищутся:
 * человека ищут по имени или телефону, не зная, на каком он этапе.
 */
export const PaletteLeadsQuery = gql`
  query PaletteLeads($query: String) {
    kommoLeads(search: $query, includeClosed: true, limit: 5) {
      items {
        id
        name
        personName
        phone
        email
        isClosed
        stage {
          id
          name
        }
      }
    }
  }
`;

/**
 * ТЗ 0106 — колонка доски «Воронка»: лиды ОДНОГО этапа под теми же
 * фильтрами, что таблица. Своя операция, а не `KommoLeadList`: поля доски
 * (бюджет, курс, «на этапе с») таблице не нужны.
 */
export const KommoLeadBoardColumnQuery = gql`
  ${KOMMO_LEAD_BOARD_FIELDS}
  query KommoLeadBoardColumn(
    $statusIds: [ID!]
    $responsible: String
    $search: String
    $includeClosed: Boolean
    $ladder: String
    $utmSource: [String!]
    $utmMedium: [String!]
    $utmCampaign: [String!]
    $fundingStatus: [String!]
    $hasFunding: Boolean
    $hasStudent: Boolean
    $createdFrom: Date
    $sort: String
    $offset: Int
    $limit: Int
  ) {
    kommoLeads(
      statusIds: $statusIds
      responsible: $responsible
      search: $search
      includeClosed: $includeClosed
      ladder: $ladder
      utmSource: $utmSource
      utmMedium: $utmMedium
      utmCampaign: $utmCampaign
      fundingStatus: $fundingStatus
      hasFunding: $hasFunding
      hasStudent: $hasStudent
      createdFrom: $createdFrom
      sort: $sort
      offset: $offset
      limit: $limit
    ) {
      items {
        ...KommoLeadBoardFields
      }
      total
    }
  }
`;

/**
 * ТЗ 0106 — число лидов и сумма бюджетов на каждом этапе под теми же
 * фильтрами (кроме этапа): шапки колонок доски и плитки-итоги.
 */
export const KommoLeadStageTotalsQuery = gql`
  query KommoLeadStageTotals(
    $responsible: String
    $search: String
    $includeClosed: Boolean
    $ladder: String
    $utmSource: [String!]
    $utmMedium: [String!]
    $utmCampaign: [String!]
    $fundingStatus: [String!]
    $hasFunding: Boolean
    $hasStudent: Boolean
    $createdFrom: Date
  ) {
    kommoLeadStageCounts(
      responsible: $responsible
      search: $search
      includeClosed: $includeClosed
      ladder: $ladder
      utmSource: $utmSource
      utmMedium: $utmMedium
      utmCampaign: $utmCampaign
      fundingStatus: $fundingStatus
      hasFunding: $hasFunding
      hasStudent: $hasStudent
      createdFrom: $createdFrom
    ) {
      statusId
      count
      priceSum
    }
  }
`;

/**
 * ТЗ 0106 — плитка «Новые за 7 дней»: сколько лидов создано в Kommo с
 * `createdFrom` под теми же фильтрами, что список (этапы включительно).
 * Только `total` — строки не нужны.
 */
export const KommoLeadsCreatedRecentlyQuery = gql`
  query KommoLeadsCreatedRecently(
    $statusIds: [ID!]
    $responsible: String
    $search: String
    $includeClosed: Boolean
    $ladder: String
    $utmSource: [String!]
    $utmMedium: [String!]
    $utmCampaign: [String!]
    $fundingStatus: [String!]
    $hasFunding: Boolean
    $hasStudent: Boolean
    $createdFrom: Date!
  ) {
    kommoLeads(
      statusIds: $statusIds
      responsible: $responsible
      search: $search
      includeClosed: $includeClosed
      ladder: $ladder
      utmSource: $utmSource
      utmMedium: $utmMedium
      utmCampaign: $utmCampaign
      fundingStatus: $fundingStatus
      hasFunding: $hasFunding
      hasStudent: $hasStudent
      createdFrom: $createdFrom
      offset: 0
      limit: 1
    ) {
      total
    }
  }
`;

/** ТЗ 0106 — причины отказа Kommo: диалог закрытия лида в 143. */
export const KommoLossReasonListQuery = gql`
  query KommoLossReasonList {
    kommoLossReasons {
      items {
        id
        name
        sort
      }
      error {
        code
        message
      }
    }
  }
`;

/** ТЗ 0106 — кого можно назначить ответственным (пользователи Kommo). */
export const KommoResponsibleListQuery = gql`
  query KommoResponsibleList {
    kommoLeadResponsibles {
      id
      kommoId
      name
      erpUserId
      erpUserName
    }
  }
`;

/**
 * ТЗ 0106 — что нужно выбору этапа и ответственного в карточке лида: строка
 * ответственного Kommo (`responsibleId`) и «на этапе с». Отдельно от
 * `KommoLeadCard`: тот спрашивают и другие экраны.
 */
export const KommoLeadAssignmentQuery = gql`
  query KommoLeadAssignment($id: ID!) {
    kommoLead(id: $id) {
      id
      responsibleId
      stageEnteredAt
      price
      courseRaw
    }
  }
`;

/**
 * ТЗ 0106 — лиды окна «Маркетинга» с именем (строка канала, кампании,
 * семейства, оффера → «Лиды (N)»). Ключи строки — как у динамики лидов:
 * `none` — нет значения, канал `unknown` — не определён.
 */
export const MarketingLeadListQuery = gql`
  query MarketingLeadList(
    $filter: MarketingFilter!
    $channel: String
    $subChannel: String
    $campaign: ID
    $family: String
    $offer: String
    $includeRepeats: Boolean
    $limit: Int
    $offset: Int
  ) {
    marketingLeads(
      filter: $filter
      channel: $channel
      subChannel: $subChannel
      campaign: $campaign
      family: $family
      offer: $offer
      includeRepeats: $includeRepeats
      limit: $limit
      offset: $offset
    ) {
      items {
        kommoLeadId
        kommoId
        personName
        stage {
          id
          name
          color
        }
        isClosed
        responsibleName
        channel
        subChannel
        campaignId
        campaignName
        familySlug
        offer
        device
        isRepeat
        createdAt
        hasContract
        contractSigned
        hasSale
        salePrice
        saleCurrency
        salePricePln
        visitsBefore
      }
      total
    }
  }
`;

/**
 * ТЗ 0106 — путь лида: визиты, страницы, формы, Kommo, договоры, продажа и
 * оплаты по точному времени. `title`/`detail` — данные, подписи переводит
 * экран по `kind`.
 */
export const LeadJourneyTimelineQuery = gql`
  query LeadJourneyTimeline($kommoLeadId: ID!) {
    leadJourney(kommoLeadId: $kommoLeadId) {
      lead {
        kommoLeadId
        kommoId
        personName
        name
        phone
        email
        stage {
          id
          name
          color
        }
        isClosed
        responsibleName
        createdAt
        courseRaw
        kommoUrl
      }
      events {
        at
        kind
        title
        detail
        channel
        tone
        meta
      }
    }
  }
`;

/**
 * ТЗ 0117 — вкладка «Aplora» карточки лида: копия карточки сделки Aplora
 * (старше 10 минут — сервер перечитывает вживую). Отдельный запрос, а не
 * поле `KommoLeadCard`: карточка лида не ждёт Aplora. `aplora: null` —
 * Aplora выключена или сделки нет — вкладка не показывается.
 */
export const LeadAploraCardQuery = gql`
  query LeadAploraCard($id: ID!) {
    kommoLead(id: $id) {
      id
      aplora {
        aploraUrl
        crmUrl
        changedAt
        syncedAt
        stale
        error
        deleted
        mergedIntoKommoId
        status
        stage
        daysInFunnel
        managerName
        scoring {
          winChance
          winTier
          leadCategory
          leadTemperature
          learnScore
          qualificationCompleteness
          dealProbability
          avgManagerScore
        }
        summaryMd
        summaryText
        outcomeSummaryText
        lastContactAt
        nextContactDate
        nextContactTip
        callsTotal
        callsAnalyzed
        learn
        managerRisks
        profile
        outcome
        dealAmount
        lossReasons
        calls {
          callId
          startedAt
          durationSeconds
          direction
          managerName
          analyzed
          baseScore
          growthScore
          scoreBand
          summaryText
        }
      }
    }
  }
`;

/**
 * ТЗ 0117 — разбор звонка Aplora вживую (раскрытие звонка во вкладке).
 * Отказ — ошибка GraphQL с кодом (`not_found`, `not_analyzed`,
 * `aplora_unavailable`, `aplora_rate_limited`).
 */
export const LeadAploraCallReviewQuery = gql`
  query LeadAploraCallReview($leadId: ID!, $callId: ID!) {
    aploraCallAnalysis(leadId: $leadId, callId: $callId) {
      callId
      aploraUrl
      startedAt
      durationSeconds
      managerName
      baseScore
      growthScore
      scoreBand
      summaryMd
      summaryText
      managerFeedback
      managerSummary
      didWell
      mistakes
      growthPoints
      nextCallRecommendations
      redFlags
    }
  }
`;

/** ТЗ 0117 — транскрипт звонка по кнопке; в кэш Apollo не кладётся. */
export const LeadAploraCallTextQuery = gql`
  query LeadAploraCallText($leadId: ID!, $callId: ID!) {
    aploraCallTranscript(leadId: $leadId, callId: $callId) {
      callId
      transcript
    }
  }
`;
